/**
 * @fileoverview Gerenciador de configuração de landpage baseado em promoções.
 * Cada seção corresponde a uma promoção com uma forma de exibição definida:
 * carrossel, grid responsivo ou sequencial.
 * Gera um JSON de configuração para ser consumido pelo componente Landpage.
 *
 * @module LandpageManager
 * @since 2.0.0
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import PreviewIcon from '@mui/icons-material/Preview';
import { selectPromotions } from '../../store/slices/globalDataSlice';
import { apiRequestJson } from '../../api/apiService';
import LandpageView from './LandpageView';
import LandpageLoadModal from './LandpageLoadModal';

/**
 * Formas de exibição disponíveis para cada seção.
 * @enum {string}
 */
const DISPLAY_MODES = {
  CAROUSEL: 'carousel',
  GRID: 'grid',
  SEQUENTIAL: 'sequential',
};

/**
 * Metadados das formas de exibição.
 */
const DISPLAY_MODE_META = {
  [DISPLAY_MODES.CAROUSEL]: {
    label: 'Carrossel',
    color: 'primary',
    description: 'Slides com navegação lateral',
    icon: ViewCarouselOutlinedIcon,
  },
  [DISPLAY_MODES.GRID]: {
    label: 'Grid Responsivo',
    color: 'secondary',
    description: 'Grade de cards adaptável à tela',
    icon: GridViewOutlinedIcon,
  },
  [DISPLAY_MODES.SEQUENTIAL]: {
    label: 'Sequencial',
    color: 'success',
    description: 'Um item abaixo do outro',
    icon: ViewStreamOutlinedIcon,
  },
};

/**
 * Gera ID único.
 * @returns {string}
 */
const generateId = () => `id_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * Cria uma nova seção com promoção e modo de exibição.
 * @param {Object} promotion - Promoção selecionada
 * @param {string} displayMode - Modo de exibição
 * @returns {Object}
 */
const createSection = (promotion, displayMode) => ({
  id: generateId(),
  promotionId: promotion.id || promotion._id,
  promotionName: promotion.name,
  displayMode,
});

// ---------------------------------------------------------------------------
// Sub-componente: card de seção configurada
// ---------------------------------------------------------------------------

/**
 * Card de exibição e controle de uma seção da landpage.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.section - Dados da seção
 * @param {Function} props.onChange - Callback ao alterar modo de exibição
 * @param {Function} props.onRemove - Callback ao remover seção
 * @param {Function} props.onMoveUp - Callback ao mover para cima
 * @param {Function} props.onMoveDown - Callback ao mover para baixo
 * @param {boolean} props.isFirst
 * @param {boolean} props.isLast
 * @returns {JSX.Element}
 */
function SectionCard({ section, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const modeMeta = DISPLAY_MODE_META[section.displayMode];
  const ModeIcon = modeMeta.icon;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        avatar={<ModeIcon color={modeMeta.color} />}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {section.promotionName}
            </Typography>
            <Chip label={modeMeta.label} color={modeMeta.color} size="small" />
          </Box>
        }
        subheader={`Promoção ID: ${section.promotionId}`}
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Mover para cima">
              <span>
                <IconButton size="small" onClick={onMoveUp} disabled={isFirst}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Mover para baixo">
              <span>
                <IconButton size="small" onClick={onMoveDown} disabled={isLast}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remover seção">
              <IconButton size="small" color="error" onClick={onRemove}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Forma de exibição</InputLabel>
          <Select
            value={section.displayMode}
            label="Forma de exibição"
            onChange={(e) => onChange({ ...section, displayMode: e.target.value })}
          >
            {/* map: renderiza opções de modo de exibição */}
            {Object.entries(DISPLAY_MODE_META).map(([value, meta]) => {
              const Icon = meta.icon;
              return (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon fontSize="small" color={meta.color} />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{meta.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{meta.description}</Typography>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

/**
 * Gerenciador de configuração de landpage baseado em promoções.
 * Cada seção corresponde a uma promoção com forma de exibição configurável.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Controla visibilidade do modal
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
export default function LandpageManager({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const allPromotions = useSelector(selectPromotions);

  const [sections, setSections] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedPromotionId, setSelectedPromotionId] = useState('');
  const [selectedDisplayMode, setSelectedDisplayMode] = useState(DISPLAY_MODES.CAROUSEL);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estado CRUD
  const [currentId, setCurrentId] = useState(null);
  const [currentName, setCurrentName] = useState('');
  const [currentActive, setCurrentActive] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * Carrega lista de landpages salvas ao abrir o manager.
   */
  useEffect(() => {
    if (open) fetchSavedList();
  }, [open]);

  /**
   * Busca todas as landpages salvas no backend.
   * @async
   */
  const fetchSavedList = async () => {
    try {
      const res = await apiRequestJson('landpage_list');
      if (res.success) setSavedList(res.data);
    } catch (err) {
      console.error('Erro ao carregar landpages:', err);
    }
  };

  /**
   * Salva (cria ou atualiza) a landpage atual no MongoDB.
   * @async
   */
  const handleSave = async () => {
    if (!currentName.trim()) {
      setSnackbar({ open: true, message: 'Informe um nome para a landpage antes de salvar.', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = { name: currentName, sections, version: '2.0', active: currentActive };
      let res;
      if (currentId) {
        res = await apiRequestJson('landpage_update', { params: { id: currentId }, body: payload });
      } else {
        res = await apiRequestJson('landpage_create', { body: payload });
        if (res.success) setCurrentId(res.data._id || res.data.id);
      }
      if (res.success) {
        setSnackbar({ open: true, message: 'Landpage salva com sucesso!', severity: 'success' });
        fetchSavedList();
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Erro ao salvar: ${err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Carrega uma landpage salva para edição e fecha o modal de carregamento.
   * @param {Object} landpage
   */
  const handleLoad = (landpage) => {
    setCurrentId(landpage._id || landpage.id);
    setCurrentName(landpage.name);
    setCurrentActive(landpage.active || false);
    setSections(landpage.sections || []);
    setLoadDialogOpen(false);
    setSnackbar({ open: true, message: `Landpage "${landpage.name}" carregada.`, severity: 'info' });
  };

  /**
   * Inicia uma nova landpage em branco.
   */
  const handleNew = () => {
    setCurrentId(null);
    setCurrentName('');
    setCurrentActive(false);
    setSections([]);
  };

  /**
   * Promoções ativas disponíveis para seleção (exclui já adicionadas).
   * filter: mantém apenas promoções ativas não usadas em seções existentes
   */
  const availablePromotions = useMemo(() => {
    const usedIds = new Set(sections.map((s) => s.promotionId));
    return allPromotions.filter(
      (p) => p.status === 'active' && !usedIds.has(p.id || p._id)
    );
  }, [allPromotions, sections]);

  /**
   * Abre dialog de adição e pré-seleciona primeira promoção disponível.
   */
  const handleOpenAddDialog = () => {
    setSelectedPromotionId(availablePromotions[0]?.id || availablePromotions[0]?._id || '');
    setSelectedDisplayMode(DISPLAY_MODES.CAROUSEL);
    setAddDialogOpen(true);
  };

  /**
   * Confirma adição da seção com a promoção e modo selecionados.
   */
  const handleAddSection = () => {
    const promotion = allPromotions.find(
      (p) => (p.id || p._id) === selectedPromotionId
    );
    if (!promotion) return;
    setSections((prev) => [...prev, createSection(promotion, selectedDisplayMode)]);
    setAddDialogOpen(false);
  };

  /**
   * Atualiza seção pelo id.
   * @param {string} id
   * @param {Object} updated
   */
  const handleSectionChange = useCallback((id, updated) => {
    // map: substitui apenas a seção alterada
    setSections((prev) => prev.map((s) => (s.id === id ? updated : s)));
  }, []);

  /**
   * Remove seção pelo id.
   * @param {string} id
   */
  const handleRemoveSection = (id) => {
    // filter: remove a seção pelo id
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  /**
   * Move seção para cima.
   * @param {number} index
   */
  const handleMoveUp = (index) => {
    if (index === 0) return;
    setSections((prev) => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };

  /**
   * Move seção para baixo.
   * @param {number} index
   */
  const handleMoveDown = (index) => {
    setSections((prev) => {
      if (index === prev.length - 1) return prev;
      const arr = [...prev];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return arr;
    });
  };

  /**
   * Gera o JSON de configuração da landpage.
   * @returns {Object}
   */
  const generateJson = () => ({
    version: '2.0',
    generatedAt: new Date().toISOString(),
    sections,
  });

  /**
   * Copia o JSON para a área de transferência.
   */
  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(generateJson(), null, 2));
    setSnackbar({ open: true, message: 'JSON copiado para a área de transferência!', severity: 'success' });
  };

  const jsonOutput = JSON.stringify(generateJson(), null, 2);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Gerenciador de Landpage
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Barra de nome e ações CRUD */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Nome da Landpage"
              value={currentName}
              onChange={(e) => setCurrentName(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 180 }}
              placeholder="Ex: Landpage Principal"
            />
            <Tooltip title={currentActive ? 'Landpage ativa (clique para desativar)' : 'Ativar como landpage principal'}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentActive}
                    onChange={(e) => setCurrentActive(e.target.checked)}
                    color="success"
                    size="small"
                  />
                }
                label={<Typography variant="caption">{currentActive ? 'Ativa' : 'Inativa'}</Typography>}
                sx={{ mx: 0 }}
              />
            </Tooltip>
            <Tooltip title="Nova landpage em branco">
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleNew}>
                Nova
              </Button>
            </Tooltip>
            <Tooltip title="Carregar landpage salva">
              <Button variant="outlined" size="small" startIcon={<FolderOpenIcon />} onClick={() => setLoadDialogOpen(true)}>
                Carregar
              </Button>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {allPromotions.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nenhuma promoção disponível. Crie promoções antes de configurar a landpage.
            </Alert>
          )}

          {sections.length === 0 && allPromotions.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Nenhuma seção adicionada. Clique em "Adicionar Seção" para começar.
            </Alert>
          )}

          {/* map: renderiza card de controle para cada seção configurada */}
          {sections.map((section, index) => (
            <SectionCard
              key={section.id}
              section={section}
              onChange={(updated) => handleSectionChange(section.id, updated)}
              onRemove={() => handleRemoveSection(section.id)}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
              isFirst={index === 0}
              isLast={index === sections.length - 1}
            />
          ))}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            disabled={availablePromotions.length === 0}
            sx={{ mt: 1 }}
          >
            Adicionar Seção
          </Button>

          {availablePromotions.length === 0 && sections.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Todas as promoções ativas já foram adicionadas.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setPreviewOpen(true)}
              disabled={sections.length === 0}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setJsonDialogOpen(true)}
              disabled={sections.length === 0}
            >
              Ver JSON
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || sections.length === 0}
            >
              {saving ? 'Salvando...' : currentId ? 'Atualizar' : 'Salvar'}
            </Button>
            <Button onClick={onClose}>Fechar</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog: selecionar promoção e modo de exibição */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Nova Seção
          <IconButton onClick={() => setAddDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Promoção</InputLabel>
            <Select
              value={selectedPromotionId}
              label="Promoção"
              onChange={(e) => setSelectedPromotionId(e.target.value)}
            >
              {/* map: renderiza promoções ativas disponíveis */}
              {availablePromotions.map((p) => (
                <MenuItem key={p.id || p._id} value={p.id || p._id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Forma de exibição</InputLabel>
            <Select
              value={selectedDisplayMode}
              label="Forma de exibição"
              onChange={(e) => setSelectedDisplayMode(e.target.value)}
            >
              {/* map: renderiza opções de modo de exibição */}
              {Object.entries(DISPLAY_MODE_META).map(([value, meta]) => {
                const Icon = meta.icon;
                return (
                  <MenuItem key={value} value={value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon fontSize="small" />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{meta.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{meta.description}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddSection} disabled={!selectedPromotionId}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: visualizar JSON gerado */}
      <Dialog open={jsonDialogOpen} onClose={() => setJsonDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          JSON de Configuração
          <IconButton onClick={() => setJsonDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.75rem',
              maxHeight: '60vh',
              fontFamily: 'monospace',
            }}
          >
            {jsonOutput}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopyJson}>
            Copiar JSON
          </Button>
          <Button onClick={() => setJsonDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de preview da landpage */}
      <LandpageView
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        config={generateJson()}
      />

      {/* Modal de carregamento de landpages salvas */}
      <LandpageLoadModal
        open={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        landpages={savedList}
        onSelect={handleLoad}
        onRefresh={fetchSavedList}
        onSnackbar={(message, severity) => setSnackbar({ open: true, message, severity })}
      />
    </>
  );
}
